import {
  getContinuousNumber,
  resetContinuousNumber,
  splitArray,
  toHumanReadable,
} from '@core/logic';
import { isSinglePointElement } from '@elements/helper';
import type { PointElement, SplineElement, SplineElementType } from '@elements/types';

export interface NetworkHierarchyView {
  id: string;
  name: string;
  elementId?: string;
  children?: NetworkHierarchyView[];
}
export type El = PointElement | SplineElementWithType;
export type NetworkHierarchy = [El, NetworkHierarchy[]];
type SplineElementWithType = SplineElement & { type: SplineElementType };

/**
 * Builds a simple hierarchy of parent and child elements, based on the parentPointElementId-prop.
 */
export const buildHierarchy = (parentId: string | null, elements: El[]): NetworkHierarchy[] => {
  if (elements.length === 0) {
    return [];
  }
  const [children, rest] = splitArray(elements, (e) => e.parentPointElementId === parentId);
  return children.map((e) => [e, buildHierarchy(e.id, rest)]);
};

/**
 * Checks if an ID is valid (not undefined, null, empty, or the string "undefined").
 */
const isValidId = (id: unknown): id is string =>
  typeof id === 'string' && id.trim() !== '' && id !== 'undefined';

/**
 * Groups children of parents with the same name using a Map for O(n) performance.
 * Also ensures all nodes have valid IDs.
 */
const groupByNodeName = (hierarchy: NetworkHierarchyView[]): NetworkHierarchyView[] => {
  if (hierarchy.length === 0) return [];

  // Group nodes by name using a Map - O(n) instead of O(n²)
  const groupedByName = new Map<string, NetworkHierarchyView[]>();

  for (const node of hierarchy) {
    const existing = groupedByName.get(node.name);
    if (existing) {
      existing.push(node);
    } else {
      groupedByName.set(node.name, [node]);
    }
  }

  // Process each group and merge nodes with same name
  const result: NetworkHierarchyView[] = [];

  for (const nodes of groupedByName.values()) {
    const firstNode = nodes[0];

    // If it's a leaf or single point element (has elementId), add all nodes individually
    if (!firstNode.children?.length || firstNode.elementId) {
      for (const node of nodes) {
        const processedChildren = node.children?.length ? groupByNodeName(node.children) : [];
        result.push({
          id: isValidId(node.id) ? node.id : getContinuousNumber(),
          name: node.name,
          elementId: node.elementId,
          children: processedChildren,
        });
      }
    } else {
      // Merge children from all nodes with same name
      const mergedChildren: NetworkHierarchyView[] = [];
      for (const node of nodes) {
        if (node.children) {
          mergedChildren.push(...node.children);
        }
      }

      result.push({
        id: isValidId(firstNode.id) ? firstNode.id : getContinuousNumber(),
        name: firstNode.name,
        children: groupByNodeName(mergedChildren),
      });
    }
  }

  // Sort for consistent ordering
  return result.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Maps the hierarchy to a tree view.
 */
const mapToTreeView = (hierarchy: NetworkHierarchy[]): NetworkHierarchyView[] => {
  const unnormalizedTreeView = hierarchy.map(([e, children]) => {
    // Ensure element has a valid ID, fallback to generated ID if undefined, null, empty string, or the string "undefined"
    const rawId = e.id;
    const isValidId =
      rawId !== undefined &&
      rawId !== null &&
      typeof rawId === 'string' &&
      rawId.trim() !== '' &&
      rawId !== 'undefined';
    const elementId = isValidId ? rawId : getContinuousNumber();

    return isSinglePointElement(e)
      ? {
          id: getContinuousNumber(),
          name: toHumanReadable(e.kind) + 's',
          children: [
            {
              id: getContinuousNumber(),
              name: toHumanReadable(e.subtype),
              children: [
                {
                  id: elementId,
                  name: e.name!,
                  elementId: elementId,
                  children: mapToTreeView(children),
                },
              ],
            },
          ],
        }
      : {
          id: getContinuousNumber(),
          name: toHumanReadable(e.type.kind) + 's',
          children: [
            {
              id: getContinuousNumber(),
              name: e.type.name,
              children: [
                {
                  id: elementId,
                  elementId: elementId,
                  name: e.name!,
                },
              ],
            },
          ],
        };
  });

  return groupByNodeName(unnormalizedTreeView);
};

export const buildElementTrees = (hierarchy: NetworkHierarchy[]): NetworkHierarchyView[] => {
  const [assigned, unassigned] = splitArray(hierarchy, ([, children]) => children.length > 0);
  resetContinuousNumber();
  return [
    { id: 'assigned-elements', name: 'Assigned', children: mapToTreeView(assigned) },
    { id: 'unassigned-elements', name: 'Unassigned', children: mapToTreeView(unassigned) },
  ];
};

export const buildHierarchyViewFromElements = (elements: El[]): NetworkHierarchyView[] =>
  buildElementTrees(
    buildHierarchy(
      null,
      elements.filter((e) => !(isSinglePointElement(e) && e.kind === 'FITTING')),
    ),
  );

const findPathToId = (
  hierarchy: NetworkHierarchyView,
  searchedId: string,
  path: string[],
): string[] => {
  if (!hierarchy.children && hierarchy.id !== searchedId) {
    return [];
  }
  if (hierarchy.id === searchedId) {
    return path;
  }
  return hierarchy.children!.flatMap((child) =>
    !child.children?.length && child.id === searchedId
      ? path // exclude leafs from the path!
      : findPathToId(child, searchedId, [...path, child.id]),
  );
};

/**
 * Find the selected element in the hierarchy and create a breadcrumb path to it.
 */
export const treesToBreadcrumbs = (
  hierarchies: NetworkHierarchyView[],
  searchedId: string,
): string[] =>
  hierarchies.flatMap((hierarchy) => findPathToId(hierarchy, searchedId, [hierarchy.id]));
